import React, { useEffect, useState } from "react"
import Button from "../../../components/ui/Button"
import Icon from "../../../components/AppIcon"
import { Card, CardContent } from "../../../components/ui/card"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../../components/ui/table"
import {
  listOrgCertificates,
  revokeCertificate,
  reinstateCertificate,
  issueCertificate,
} from "../../../utils/certHelpers"
import { exportCredentialsCsv } from "../../../utils/csvExportHelpers"
import {
  listPendingCredentialRequests,
  approveCredentialRequest,
  resolveCredentialRequest,
} from "../../../utils/credentialRequestHelpers"
import { getCredentialTemplates } from "../../../utils/credentialTemplateHelpers"

const inputClass =
  "w-full rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground/60 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
const labelClass = "text-sm font-medium text-foreground mb-1.5 block"

const StatCard = ({ label, value, icon }) => (
  <Card className="p-4 dark:bg-transparent">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold text-foreground mt-0.5">{value}</p>
      </div>
      <Icon name={icon} size={18} className="text-muted-foreground" />
    </div>
  </Card>
)

const emptyIssueForm = () => ({
  templateId: "",
  learnerUserId: "",
  learnerName: "",
  courseId: "",
  courseTitle: "",
  score: "",
})

const CertificatesPanel = ({ organization }) => {
  const orgId = organization?.orgId
  const [certificates, setCertificates] = useState([])
  const [pendingRequests, setPendingRequests] = useState([])
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState("list") // "list" | "issue"
  const [statusFilter, setStatusFilter] = useState("all") // all | valid | revoked
  const [issueForm, setIssueForm] = useState(emptyIssueForm())
  const [requestTemplateChoice, setRequestTemplateChoice] = useState({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [copiedId, setCopiedId] = useState(null)

  const loadAll = async () => {
    if (!orgId) return
    setLoading(true)
    const [{ certificates: certs }, { requests }, { templates: tpls }] = await Promise.all([
      listOrgCertificates(orgId),
      listPendingCredentialRequests(orgId),
      getCredentialTemplates(orgId),
    ])
    setCertificates(certs || [])
    setPendingRequests(requests || [])
    setTemplates(tpls || [])
    setLoading(false)
  }

  useEffect(() => {
    loadAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  const templateById = (id) => templates.find((t) => t.id === id)

  const handleRevoke = async (certId) => {
    if (!window.confirm("Revoke this credential? It will immediately show as invalid on verification.")) return
    await revokeCertificate(certId, orgId)
    loadAll()
  }

  const handleReinstate = async (certId) => {
    await reinstateCertificate(certId)
    loadAll()
  }

  const handleCopyLink = async (certId) => {
    const url = `${window.location.origin}/verify/${certId}`
    try {
      await navigator.clipboard.writeText(url)
      setCopiedId(certId)
      setTimeout(() => setCopiedId(null), 1500)
    } catch {
      window.prompt("Copy verification link:", url)
    }
  }

  const handleApproveRequest = async (request) => {
    const chosenTemplateId = requestTemplateChoice[request.requestId]
    const template = chosenTemplateId ? templateById(chosenTemplateId) : null
    setSaving(true)
    setError("")
    const { error: err } = await approveCredentialRequest(request, {
      orgName: organization?.name,
      orgVerified: organization?.verified,
      template,
    })
    setSaving(false)
    if (err) {
      setError(err.message || "Could not issue credential")
      return
    }
    loadAll()
  }

  const handleRejectRequest = async (request) => {
    await resolveCredentialRequest(request.requestId, "rejected", orgId)
    loadAll()
  }

  const handleManualIssue = async () => {
    if (!issueForm.learnerUserId.trim() || !issueForm.courseId.trim()) {
      setError("Learner user ID and course ID are required.")
      return
    }
    setSaving(true)
    setError("")
    const template = issueForm.templateId ? templateById(issueForm.templateId) : null
    const { error: err } = await issueCertificate({
      userId: issueForm.learnerUserId.trim(),
      userName: issueForm.learnerName.trim(),
      courseId: issueForm.courseId.trim(),
      courseTitle: issueForm.courseTitle.trim(),
      orgId,
      orgName: organization?.name,
      orgVerified: organization?.verified,
      score: issueForm.score ? Number(issueForm.score) : 0,
      templateId: template?.id || null,
      template,
    })
    setSaving(false)
    if (err) {
      setError(err.message || "Could not issue credential")
      return
    }
    setIssueForm(emptyIssueForm())
    setView("list")
    loadAll()
  }

  const issuedCount = certificates.filter((c) => c.status !== "revoked").length
  const revokedCount = certificates.filter((c) => c.status === "revoked").length
  const filteredCertificates =
    statusFilter === "all" ? certificates : certificates.filter((c) => c.status === statusFilter)

  const handleExportCsv = () => {
    exportCredentialsCsv(filteredCertificates, organization?.name)
  }

  if (loading) {
    return <div className="p-12 text-center text-muted-foreground text-sm">Loading credentials...</div>
  }

  // ---------- ISSUE VIEW ----------
  if (view === "issue") {
    return (
      <div className="space-y-5 max-w-xl">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" iconName="ArrowLeft" onClick={() => setView("list")}>
            Back
          </Button>
          <h2 className="font-semibold text-foreground">Issue Credential</h2>
        </div>

        {error && <div className="rounded-md bg-error/10 text-error text-sm px-3 py-2">{error}</div>}

        <div>
          <label className={labelClass}>Template</label>
          <select
            className={`${inputClass} [color-scheme:light] dark:[color-scheme:dark]`}
            value={issueForm.templateId}
            onChange={(e) => setIssueForm((f) => ({ ...f, templateId: e.target.value }))}
          >
            <option value="">No template (plain credential)</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          {templates.length === 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              You haven't created a credential template yet — this will issue with default styling.
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Learner user ID</label>
            <input
              className={inputClass}
              value={issueForm.learnerUserId}
              onChange={(e) => setIssueForm((f) => ({ ...f, learnerUserId: e.target.value }))}
              placeholder="From the Learners page"
            />
          </div>
          <div>
            <label className={labelClass}>Learner name</label>
            <input
              className={inputClass}
              value={issueForm.learnerName}
              onChange={(e) => setIssueForm((f) => ({ ...f, learnerName: e.target.value }))}
              placeholder="Jane Doe"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Course ID</label>
            <input
              className={inputClass}
              value={issueForm.courseId}
              onChange={(e) => setIssueForm((f) => ({ ...f, courseId: e.target.value }))}
            />
          </div>
          <div>
            <label className={labelClass}>Course title</label>
            <input
              className={inputClass}
              value={issueForm.courseTitle}
              onChange={(e) => setIssueForm((f) => ({ ...f, courseTitle: e.target.value }))}
            />
          </div>
        </div>

        <div>
          <label className={labelClass}>Score (optional)</label>
          <input
            type="number"
            className={inputClass}
            value={issueForm.score}
            onChange={(e) => setIssueForm((f) => ({ ...f, score: e.target.value }))}
            placeholder="e.g. 92"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => setView("list")}>
            Cancel
          </Button>
          <Button onClick={handleManualIssue} loading={saving}>
            Issue Credential
          </Button>
        </div>
      </div>
    )
  }

  // ---------- LIST VIEW ----------
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-xl">
          Credentials issued by {organization?.name || "your organization"} — automatically from course
          capstones, or manually below.
        </p>
        <div className="flex gap-2 shrink-0">
          <Button
            variant="outline"
            iconName="Download"
            onClick={handleExportCsv}
            disabled={filteredCertificates.length === 0}
          >
            Export CSV
          </Button>
          <Button iconName="Plus" onClick={() => setView("issue")}>
            Issue Credential
          </Button>
        </div>
      </div>

      {error && <div className="rounded-md bg-error/10 text-error text-sm px-3 py-2">{error}</div>}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <StatCard label="Issued" value={issuedCount} icon="Award" />
        <StatCard label="Pending Requests" value={pendingRequests.length} icon="Clock" />
        <StatCard label="Revoked" value={revokedCount} icon="ShieldOff" />
      </div>

      {pendingRequests.length > 0 && (
        <Card className="dark:bg-transparent">
          <CardContent className="p-4 space-y-3">
            <h3 className="font-semibold text-sm text-foreground">Pending Credential Requests</h3>
            {pendingRequests.map((req) => (
              <div
                key={req.requestId}
                className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 py-2 border-b border-border last:border-0"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{req.userName || req.userId}</p>
                  <p className="text-xs text-muted-foreground truncate">{req.courseTitle}</p>
                </div>
                <select
                  className={`${inputClass} sm:w-48 [color-scheme:light] dark:[color-scheme:dark]`}
                  value={requestTemplateChoice[req.requestId] || ""}
                  onChange={(e) =>
                    setRequestTemplateChoice((prev) => ({ ...prev, [req.requestId]: e.target.value }))
                  }
                >
                  <option value="">No template</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
                <div className="flex gap-2">
                  <Button size="xs" onClick={() => handleApproveRequest(req)} loading={saving}>
                    Approve
                  </Button>
                  <Button size="xs" variant="outline" onClick={() => handleRejectRequest(req)}>
                    Reject
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="flex gap-1 border-b border-border">
        {[
          { key: "all", label: "All" },
          { key: "valid", label: "Issued" },
          { key: "revoked", label: "Revoked" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              statusFilter === tab.key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {filteredCertificates.length === 0 ? (
        <Card className="p-12 text-center dark:bg-transparent">
          <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Award" size={24} className="text-accent" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-1">No credentials yet</h3>
          <p className="text-muted-foreground text-sm">
            Credentials appear here automatically once learners pass a course's capstone, or you can issue
            one manually.
          </p>
        </Card>
      ) : (
        <Card className="p-0 dark:bg-transparent overflow-x-auto">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-5">Learner</TableHead>
                  <TableHead>Course</TableHead>
                  <TableHead>Template</TableHead>
                  <TableHead className="text-right">Cert ID</TableHead>
                  <TableHead className="text-right">Score</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                  <TableHead className="text-right pr-5">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCertificates.map((cert) => {
                  const tpl = cert.templateSnapshot
                  return (
                    <TableRow key={cert?.certId}>
                      <TableCell className="pl-5 font-medium truncate">{cert?.issuedTo}</TableCell>
                      <TableCell className="text-muted-foreground truncate">{cert?.courseTitle}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {tpl ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span
                              className="h-2 w-2 rounded-full"
                              style={{ backgroundColor: tpl.design?.primaryColor || "#4F46E5" }}
                            />
                            {tpl.name}
                          </span>
                        ) : (
                          <span className="text-xs">Default</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs text-muted-foreground">
                        {cert?.certId}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">{cert?.score}%</TableCell>
                      <TableCell className="text-right">
                        <span
                          className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                            cert.status === "revoked"
                              ? "bg-error/10 text-error"
                              : "bg-success/10 text-success"
                          }`}
                        >
                          {cert.status === "revoked" ? "Revoked" : "Valid"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right pr-5">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="xs"
                            variant="ghost"
                            iconName={copiedId === cert.certId ? "Check" : "Link"}
                            onClick={() => handleCopyLink(cert.certId)}
                          />
                          {cert.status === "revoked" ? (
                            <Button size="xs" variant="ghost" iconName="RotateCcw" onClick={() => handleReinstate(cert.certId)} />
                          ) : (
                            <Button size="xs" variant="ghost" iconName="ShieldOff" onClick={() => handleRevoke(cert.certId)} />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default CertificatesPanel